import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const safetyFAQs = [
  {
    question: 'Is the Superbuy Spreadsheet safe to use?',
    answer:
      'Yes, the Superbuy Spreadsheet is safe when used correctly. The spreadsheet is simply a curated collection of product links from legitimate Chinese marketplaces. It does not handle payments, store personal data, or require you to download any software. The safety risks come from the individual sellers and how you choose to make purchases — which is why following trusted shopping practices is so important.',
  },
  {
    question: 'Can I get scammed through spreadsheet links?',
    answer:
      'While the spreadsheet links are verified by community members, scams can still happen at the seller level. A seller might ship a lower-quality batch, send the wrong item, or in rare cases not ship at all. This is why using a reputable shopping agent is critical — agents provide buyer protection, hold your funds until items arrive at their warehouse, and allow you to request refunds if problems arise.',
  },
  {
    question: 'What payment methods are safest?',
    answer:
      'Always pay through your shopping agent platform rather than directly to sellers. Reputable agents accept PayPal, credit cards, and other secure payment methods that offer buyer protection. Never pay sellers directly via bank transfer, Western Union, or cryptocurrency — these payment methods offer little to no recourse if something goes wrong.',
  },
  {
    question: 'How can I tell if a seller in the spreadsheet is trustworthy?',
    answer:
      'Look for sellers with high transaction counts and good ratings on their marketplace profiles. Check if community members have left positive feedback about the seller in spreadsheet notes. Prioritize products that include QC photo examples and have been recently verified. If a deal seems too good to be true — a premium item at an extremely low price — approach with extra caution.',
  },
];

export default function SafetyGuidePage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <>
      <Navbar />
      <main>
        <article>
          <section className="relative w-full min-h-[400px] md:min-h-[500px] flex items-center overflow-hidden">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Shield%20icon%20symbol%20placed%20among%20neatly%20arranged%20fashion%20accessories%20on%20light%20sand%20colored%20textured%20surface%20soft%20diffused%20studio%20lighting%20trust%20protection%20safety%20concept%20minimal%20editorial%20product%20photography%20clean%20composition%20warm%20tones&width=1600&height=700&seq=safety-hero-v2-x2&orientation=landscape')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-50/50 via-background-50/30 to-background-50/80"></div>
            <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-12">
              <div className="max-w-3xl mx-auto text-center">
                <nav className="mb-5 text-xs md:text-sm text-foreground-500">
                  <a href="/" className="hover:text-primary-600 transition-colors">Home</a>
                  <span className="mx-2">›</span>
                  <span className="text-foreground-700">Guides</span>
                  <span className="mx-2">›</span>
                  <span className="text-primary-600 font-medium">Safety Guide</span>
                </nav>
                <span className="inline-block px-3 py-1 rounded-full border border-accent-300/50 bg-accent-100/60 text-accent-700 text-xs font-medium mb-4">
                  Trust &amp; Security
                </span>
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight mb-4">
                  Is Superbuy Spreadsheet Safe? Complete Safety Guide
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  Everything you need to know about safe shopping practices, avoiding scams, and protecting yourself when using spreadsheet product links.
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom text-sm md:text-base leading-relaxed text-foreground-700 space-y-7">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Understanding the Safety Landscape
                  </h2>
                  <p>
                    One of the most common questions from newcomers is whether the{' '}
                    <strong>Superbuy Spreadsheet</strong> itself is safe. The short answer is:
                    the spreadsheet is just a database — it does not process payments, collect
                    personal information, or require you to install anything on your device.
                    The real safety considerations come from how you interact with the sellers
                    and agents behind the product links.
                  </p>
                  <p className="mt-3">
                    Think of the spreadsheet as a library catalog. The catalog itself cannot harm
                    you, but the books it references vary in quality. Similarly, the spreadsheet
                    links to sellers on platforms like Taobao and Weidian — legitimate marketplaces
                    with millions of transactions daily. The key is knowing which sellers to trust
                    and which red flags to watch for before completing a purchase.
                  </p>
                  <p className="mt-3">
                    By following a few straightforward safety practices, you can shop through the
                    spreadsheet with the same level of confidence you would have on any major
                    e-commerce platform. This guide covers everything from choosing the right agent
                    to recognizing suspicious listings, so you can focus on finding great products
                    without worrying about getting scammed.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Always Use a Trusted Shopping Agent
                  </h2>
                  <p>
                    A shopping agent is the single most important safety tool in your spreadsheet
                    shopping arsenal. Agents act as intermediaries between you and the seller. They
                    purchase items on your behalf, receive them at their warehouse, take QC photos,
                    and ship internationally. More importantly, they hold your funds in escrow and
                    provide buyer protection that direct purchasing cannot offer.
                  </p>
                  <p className="mt-3">
                    When you pay through a reputable agent, your money is not released to the seller
                    until the item arrives at the agent's warehouse and passes inspection. If the
                    seller sends the wrong item, a defective product, or nothing at all, the agent
                    can facilitate a refund or exchange. Without an agent, you would be navigating
                    a foreign marketplace in a different language with limited recourse.
                  </p>

                  <div className="overflow-x-auto mt-5">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b-2 border-background-300">
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">Shopping Method</th>
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">Buyer Protection</th>
                          <th className="text-left py-3 pr-4 text-foreground-800 font-semibold">Risk Level</th>
                          <th className="text-left py-3 text-foreground-800 font-semibold">Recommendation</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Direct from Seller</td>
                          <td className="py-3 pr-4 text-red-500">Minimal</td>
                          <td className="py-3 pr-4 text-red-500">High</td>
                          <td className="py-3 text-red-500">Not Recommended</td>
                        </tr>
                        <tr className="border-b border-background-200">
                          <td className="py-3 pr-4 text-foreground-600 font-medium">Small Unknown Agent</td>
                          <td className="py-3 pr-4 text-foreground-500">Limited</td>
                          <td className="py-3 pr-4 text-foreground-500">Medium</td>
                          <td className="py-3 text-foreground-500">Use With Caution</td>
                        </tr>
                        <tr>
                          <td className="py-3 pr-4 text-primary-700 font-semibold">Reputable Established Agent</td>
                          <td className="py-3 pr-4 text-primary-600 font-medium">Strong</td>
                          <td className="py-3 pr-4 text-primary-600 font-medium">Low</td>
                          <td className="py-3 text-primary-600 font-medium">Highly Recommended</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-background-950 rounded-2xl p-6 md:p-8 text-center my-8">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-background-50 mb-3">
                    Shop Safely With Verified Products
                  </h3>
                  <p className="text-xs md:text-sm text-background-300 mb-5 max-w-md mx-auto">
                    Access the latest spreadsheet with community-vetted product links and trusted recommendations.
                  </p>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Buy It Now
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </div>
                  </a>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Red Flags to Watch For
                  </h2>
                  <p>
                    Even with a trusted agent, you should still evaluate each product link before
                    purchasing. Here are the most common warning signs that should make you pause
                    and investigate further before committing to a purchase.
                  </p>

                  <div className="space-y-3 mt-4">
                    <div className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                      <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-red-100 text-red-500">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      <div>
                        <strong className="text-foreground-900 text-sm">Prices That Are Suspiciously Low</strong>
                        <p className="text-foreground-600 text-xs mt-1">
                          If a product is listed at a fraction of the typical market price, it is
                          likely bait-and-switch. The seller may ship a much lower quality version
                          than what the listing photos show.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                      <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-red-100 text-red-500">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      <div>
                        <strong className="text-foreground-900 text-sm">Seller With No Transaction History</strong>
                        <p className="text-foreground-600 text-xs mt-1">
                          New sellers with zero or very few completed transactions are higher risk.
                          Stick with sellers who have established histories and positive ratings on
                          the marketplace platform.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                      <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-red-100 text-red-500">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      <div>
                        <strong className="text-foreground-900 text-sm">Stock Photos Instead of Real Product Images</strong>
                        <p className="text-foreground-600 text-xs mt-1">
                          If a listing only uses polished stock or brand photos without any real-life
                          shots, the actual product may look completely different. Look for listings
                          that include genuine customer or QC photographs.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 p-4 rounded-xl bg-background-100 border border-background-200">
                      <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-red-100 text-red-500">
                        <i className="ri-error-warning-line"></i>
                      </div>
                      <div>
                        <strong className="text-foreground-900 text-sm">Pressure to Pay Outside the Platform</strong>
                        <p className="text-foreground-600 text-xs mt-1">
                          Any seller asking you to pay via direct transfer, cryptocurrency, or outside
                          the agent platform is a major red flag. Always keep transactions within the
                          protected payment system your agent provides.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Safe Shopping Checklist
                  </h2>
                  <p>
                    Before finalizing any purchase through the{' '}
                    <strong>Superbuy Spreadsheet</strong>, run through this quick safety checklist
                    to ensure you are protected at every step of the process.
                  </p>

                  <div className="space-y-3 mt-4">
                    {[
                      'Verify the seller has a good rating and transaction history on the marketplace.',
                      'Confirm the product link still works and leads to the correct listing.',
                      'Check for recent QC photos or community feedback in the spreadsheet notes.',
                      'Use a reputable, established shopping agent for every purchase.',
                      'Review QC photos carefully before approving international shipment.',
                      'Keep all transaction records and communication within the agent platform.',
                      'Never share personal payment information directly with sellers.',
                      'If something feels wrong, trust your instincts and skip the purchase.',
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5">
                          <i className="ri-check-line text-xs"></i>
                        </div>
                        <span className="text-sm text-foreground-600">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3">
                    {safetyFAQs.map((faq, index) => (
                      <div
                        key={index}
                        className="rounded-2xl bg-background-100 border border-background-200 overflow-hidden"
                      >
                        <button
                          onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                        >
                          <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">
                            {faq.question}
                          </span>
                          <div className={`w-5 h-5 shrink-0 flex items-center justify-center transition-transform duration-300 ${openFAQ === index ? 'rotate-180' : ''}`}>
                            <i className="ri-arrow-down-s-line text-foreground-500"></i>
                          </div>
                        </button>
                        <div className={`overflow-hidden transition-all duration-300 ${openFAQ === index ? 'max-h-96 pb-4' : 'max-h-0'}`}>
                          <p className="px-5 text-xs md:text-sm text-foreground-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4">
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mb-4">
                    Shop With Peace of Mind
                  </h2>
                  <p>
                    The <strong>Superbuy Spreadsheet</strong> community has spent years refining safe
                    shopping practices, and by following the guidelines in this article, you can
                    enjoy all the benefits of spreadsheet shopping without the worry. The combination
                    of a trusted agent, careful seller evaluation, and thorough QC review creates a
                    safety system that protects you at every stage of the process.
                  </p>
                  <p className="mt-3">
                    Remember that the spreadsheet itself is a tool — how safely you use it depends
                    on the practices you follow. Stay informed, trust verified sources, and never
                    rush into a purchase that raises even a small doubt in your mind. Smart shoppers
                    are safe shoppers.
                  </p>
                </div>

                <div className="pt-6 pb-4 border-t border-background-200 flex flex-wrap gap-4 items-center justify-between">
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors font-medium"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-sm"></i>
                    </div>
                    Back to Superbuy Spreadsheet Home
                  </a>
                  <a
                    href="/qc-guide"
                    className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors font-medium"
                  >
                    Read: QC Guide
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>

        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h3 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 text-center mb-8">
              Continue Reading
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
              {[
                {
                  title: 'Complete Spreadsheet Guide',
                  desc: 'Master the entire shopping workflow',
                  link: '/superbuy-spreadsheet-guide',
                  img: 'https://readdy.ai/api/search-image?query=Laptop%20on%20wooden%20desk%20with%20organized%20spreadsheet%20view%20surrounded%20by%20neatly%20folded%20fashion%20pieces%20morning%20sunlight%20through%20window%20warm%20tones%20editorial%20product%20photography%20clean%20minimal%20composition&width=400&height=280&seq=rel-guide-safety-v2-x2&orientation=landscape',
                },
                {
                  title: 'QC Guide for Beginners',
                  desc: 'Inspect products like an expert',
                  link: '/qc-guide',
                  img: 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20over%20clothing%20tag%20with%20fabric%20texture%20detail%20soft%20studio%20lighting%20on%20ivory%20surface%20product%20inspection%20quality%20check%20concept%20minimal%20editorial%20photography%20clean%20composition&width=400&height=280&seq=rel-qc-safety-v2-x2&orientation=landscape',
                },
                {
                  title: 'Shipping &amp; Delivery Tips',
                  desc: 'Navigate international shipping',
                  link: '/superbuy-spreadsheet-guide',
                  img: 'https://readdy.ai/api/search-image?query=Cardboard%20box%20with%20fashion%20items%20peeking%20out%20placed%20on%20warm%20beige%20surface%20soft%20natural%20light%20from%20window%20minimal%20editorial%20product%20photography%20clean%20composition%20unboxing%20concept&width=400&height=280&seq=rel-ship-safety-v2-x2&orientation=landscape',
                },
              ].map((item) => (
                <a
                  key={item.title}
                  href={item.link}
                  className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: `url('${item.img}')` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/65 via-foreground-950/20 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col justify-end p-5">
                    <h4 className="text-background-50 font-heading text-lg font-semibold leading-snug mb-1">
                      {item.title}
                    </h4>
                    <p className="text-background-50/75 text-xs">{item.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-950 mb-4">
              Start Shopping Safely Today
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-md mx-auto">
              Access the latest Superbuy Spreadsheet with confidence — every link community-vetted for quality.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-foreground-500 transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Superbuy Sheet
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap w-full sm:w-auto justify-center"
              >
                Buy It Now
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-sm"></i>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'Is Superbuy Spreadsheet Safe? Complete Safety Guide 2026',
            description:
              'Everything you need to know about safe shopping practices, avoiding scams, and protecting yourself when using Superbuy Spreadsheet product links.',
            datePublished: '2026-04-10',
            dateModified: '2026-07-13',
            author: {
              '@type': 'Organization',
              name: 'Superbuy Spread',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Guides',
                item: (import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos') + '/',
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: 'Safety Guide',
                item: (import.meta.env.VITE_SITE_URL || 'https://superbuyspread.autos') + '/safety-guide',
              },
            ],
          }),
        }}
      />
    </>
  );
}